import IconLock from '@apollo/icons/default/IconLock.svg';
import IconTrash from '@apollo/icons/default/IconTrash.svg';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Group,
  IconButton,
  Input,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import classnames from 'classnames';
import { sortBy } from 'lodash';
import React, { useState } from 'react';

import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';

import { ExcludedOperationName } from '../transforms';

export interface Props {
  graphId: string;
  excludedOperationNames: ExcludedOperationName[] | null;
  appendedExcludedOperationNames?: ExcludedOperationName[];
  onNewExcludedOperationName: (name: string) => void;
  onExcludedOperationNameRemove: (name: string) => void;
}

// https://spec.graphql.org/June2018/#sec-Names
const OPERATION_NAME_VALIDATION = /^[_A-Za-z][_0-9A-Za-z]*$/;
export const errorMessages = {
  invalidOperation: 'A valid operation name is required',
  duplicateOperation: 'Operation has already been excluded',
};

export const ExcludedOperationsTable = ({
  graphId,
  excludedOperationNames,
  appendedExcludedOperationNames,
  onNewExcludedOperationName,
  onExcludedOperationNameRemove,
}: Props) => {
  const [newOperation, setNewOperation] = useState<string>('');
  const [error, setError] = useState<string>('');

  const addNewOperation = () => {
    if (Boolean(newOperation) && !error) {
      onNewExcludedOperationName(newOperation);
      setNewOperation('');
    }
  };

  const updateNewOperation = (value: string) => {
    if (!OPERATION_NAME_VALIDATION.test(value)) {
      setError(errorMessages.invalidOperation);
    } else if (excludedOperationNames?.some(({ name }) => value === name)) {
      setError(errorMessages.duplicateOperation);
    } else {
      setError('');
    }
    setNewOperation(String(value));
  };

  const allOperationNames: {
    name: string;
    isAppended: boolean;
  }[] = sortBy(
    [
      ...(excludedOperationNames?.map((op) => ({
        name: op.name,
        isAppended: false,
      })) ?? []),
      ...(appendedExcludedOperationNames?.map((op) => ({
        name: op.name,
        isAppended: true,
      })) ?? []),
    ],
    (op) => op.name,
  );

  const emptyState: boolean = allOperationNames.length === 0;
  const showActions = allOperationNames.some(({ isAppended }) => !isAppended);

  return (
    <>
      {!emptyState && (
        <Table>
          <Thead>
            <Tr>
              <Th>Operation name</Th>
              {showActions && (
                <Th isActionsMenu>
                  <span className="sr-only">Actions</span>
                </Th>
              )}
            </Tr>
          </Thead>
          <Tbody>
            {allOperationNames.map(({ name, isAppended }) => {
              return (
                <Tr key={`${name}_${isAppended}`}>
                  <Td>
                    <Group gap={2} data-testid={`row-operation-${name}`}>
                      {name}
                      {isAppended && <IconLock className="text-icon-primary" />}
                    </Group>
                  </Td>
                  {showActions && (
                    <Td isActionsMenu>
                      {!isAppended && (
                        <IconButton
                          aria-label="Exclude operation"
                          data-testid={`delete-${name}`}
                          variant="secondary"
                          onClick={() => onExcludedOperationNameRemove(name)}
                        >
                          <IconTrash />
                        </IconButton>
                      )}
                    </Td>
                  )}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      )}
      <div
        className="flex w-full justify-end px-[--card-body-padding-x]"
        data-testid="new-operation"
      >
        <div
          className={classnames(
            'flex w-full items-start',
            // Transition from empty state.
            'transition-all ease-in',
            !emptyState && 'max-w-lg',
            emptyState && 'max-w-full',
          )}
        >
          <div className="grow">
            <FormControl isInvalid={!!(newOperation && error)} isRequired>
              <FormLabel className="sr-only">Excluded operation name</FormLabel>
              <Input
                onChange={(e) => updateNewOperation(e.target.value)}
                className="w-full"
                placeholder="Enter new excluded operation name..."
                value={newOperation}
              />
              <FormErrorMessage>{error}</FormErrorMessage>
            </FormControl>
          </div>

          <div className="ml-3">
            <PermissionWarning
              graphId={graphId}
              graphPermissions="canWriteCheckConfiguration"
              childRenderer={(hasPermission) => (
                <Button
                  onClick={addNewOperation}
                  isDisabled={!newOperation || !hasPermission || Boolean(error)}
                >
                  Exclude Operation
                </Button>
              )}
            />
          </div>
        </div>
      </div>
    </>
  );
};
